/**
 * Seat map family discount.
 * Must stay identical to one-evenement-functions/src/familyDiscount.ts, which validates the prices at checkout.
 */

export interface FamilyDiscountOption {
  priceTag: string
  quantity: number
}

export interface FamilyDiscountConfig {
  isEnabled?: boolean
  percentage?: number
  combinations?: { items?: FamilyDiscountOption[] }[]
  banner?: Record<string, string>
}

export interface FamilyDiscountApplication {
  ticketTypeId: string
  percentage: number
  quantities: { priceTag: string, quantity: number }[]
  amount: number
}

const PRICE_TAG_ENABLED_FIELDS: Record<string, string | null> = {
  normal: null,
  student: 'isStudentDiscount',
  children: 'isChildrenDiscount',
  youth: 'isYouthDiscount',
  senior: 'isSeniorDiscount',
  disabled: 'isDisabledDiscount'
}

const roundToCents = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100

/** Returns whether a price tag can be used in a family discount condition of the ticket type. */
function isFamilyDiscountPriceTagActive(priceTag: string, ticketTypeData: any): boolean {
  if (!Object.prototype.hasOwnProperty.call(PRICE_TAG_ENABLED_FIELDS, priceTag))
    return false

  const enabledField = PRICE_TAG_ENABLED_FIELDS[priceTag]
  return enabledField === null || ticketTypeData?.[enabledField] === true
}

/**
 * Returns a normalized active family discount, or null when it is disabled, invalid or not supported by the ticket type.
 * Each combination maps a price tag to its required quantity: its tickets are combined with AND, the combinations with OR.
 */
export function getFamilyDiscount(ticketTypeData: any): { percentage: number, combinations: Map<string, number>[] } | null {
  const config: FamilyDiscountConfig | undefined = ticketTypeData?.familyDiscount
  if (config?.isEnabled !== true || ticketTypeData?.isCombinedTicket === true)
    return null

  const percentage = config.percentage
  if (typeof percentage !== 'number' || !Number.isFinite(percentage) || percentage < 1 || percentage > 100)
    return null

  if (!Array.isArray(config.combinations) || config.combinations.length === 0)
    return null

  const combinations: Map<string, number>[] = []
  for (const combination of config.combinations) {
    const items = Array.isArray(combination?.items) ? combination.items : []
    if (items.length === 0)
      return null

    const quantities = new Map<string, number>()
    for (const item of items) {
      if (typeof item?.priceTag !== 'string' || !isFamilyDiscountPriceTagActive(item.priceTag, ticketTypeData) || !Number.isInteger(item.quantity) || item.quantity < 1)
        return null

      quantities.set(item.priceTag, (quantities.get(item.priceTag) || 0) + item.quantity)
    }
    combinations.push(quantities)
  }

  return { percentage, combinations }
}

/**
 * Finds the combination that fits in the available tickets and gives the lowest discount.
 * Ties are resolved by the combinations order configured in the admin.
 */
function findFamilySet(combinations: Map<string, number>[], available: Map<string, number>, unitPrices: Map<string, number>): Map<string, number> | null {
  let best: Map<string, number> | null = null
  let bestValue = 0

  for (const combination of combinations) {
    let value = 0
    let fits = true
    for (const [priceTag, quantity] of combination) {
      if ((available.get(priceTag) || 0) < quantity) {
        fits = false
        break
      }
      value += quantity * (unitPrices.get(priceTag) || 0)
    }

    if (fits && (best === null || value < bestValue)) {
      best = combination
      bestValue = value
    }
  }

  return best
}

/**
 * Applies the seat map family discount of each ticket type to its seat cart items.
 * The discount is applied as many times as the configured combination fits in the cart; other items keep their price.
 * Sets familyDiscountPrice (unit price after family discount) and familyDiscountPercentage on discounted items.
 * Returns one entry per ticket type whose family discount applies.
 */
export function applyFamilyDiscountToCart(cart: any[], ticketTypesById: Map<string, any>): FamilyDiscountApplication[] {
  const applications: FamilyDiscountApplication[] = []
  const seatItemsByTicketType = new Map<string, any[]>()
  for (const item of cart) {
    delete item.familyDiscountPrice
    delete item.familyDiscountPercentage

    if (!item?.ticketTypeId || typeof item.datePromotionPrice === 'number' || !(item.seat?.id || item.seatId) || item.quantity !== 1)
      continue

    const items = seatItemsByTicketType.get(item.ticketTypeId) || []
    items.push(item)
    seatItemsByTicketType.set(item.ticketTypeId, items)
  }

  seatItemsByTicketType.forEach((items, ticketTypeId) => {
    const discount = getFamilyDiscount(ticketTypesById.get(ticketTypeId))
    if (!discount)
      return

    const available = new Map<string, number>()
    const unitPrices = new Map<string, number>()
    for (const item of items) {
      available.set(item.priceTag, (available.get(item.priceTag) || 0) + 1)
      unitPrices.set(item.priceTag, item.price || 0)
    }

    const discountedQuantities = new Map<string, number>()
    let familySet: Map<string, number> | null
    while ((familySet = findFamilySet(discount.combinations, available, unitPrices)) !== null) {
      familySet.forEach((quantity, priceTag) => {
        available.set(priceTag, (available.get(priceTag) || 0) - quantity)
        discountedQuantities.set(priceTag, (discountedQuantities.get(priceTag) || 0) + quantity)
      })
    }

    if (discountedQuantities.size === 0)
      return

    const application: FamilyDiscountApplication = {
      ticketTypeId,
      percentage: discount.percentage,
      quantities: Array.from(discountedQuantities.entries()).map(([priceTag, quantity]) => ({ priceTag, quantity })),
      amount: 0
    }

    for (const item of items) {
      const remaining = discountedQuantities.get(item.priceTag) || 0
      if (remaining <= 0)
        continue

      discountedQuantities.set(item.priceTag, remaining - 1)
      item.familyDiscountPrice = roundToCents((item.price || 0) * (1 - discount.percentage / 100))
      item.familyDiscountPercentage = discount.percentage
      application.amount += (item.price || 0) - item.familyDiscountPrice
    }

    application.amount = roundToCents(application.amount)
    applications.push(application)
  })

  return applications
}

/** Returns the unit price after the automatic date promotion or package discount, used as the promo code base. */
export function getFamilyDiscountedUnitPrice(item: any): number {
  if (typeof item?.datePromotionPrice === 'number')
    return item.datePromotionPrice
  return (typeof item?.familyDiscountPrice === 'number') ? item.familyDiscountPrice : item.price
}
